import React, { useState, useEffect } from "react";
import { useTheme } from "@/contexts/ThemeContext.js";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGear, faFlask, faDiagramProject, faBookOpen, faRobot, faCheck, faInfoCircle, faSpinner, faArrowRight, faFileContract, faExchangeAlt } from "@awesome.me/kit-269fbef2e2/icons/duotone/solid";
import companyService from "@/services/companyService";
import { toast } from 'sonner';
import { mcmService } from "@/services/mcmService";
import assetsService from "@/services/assetsService";
import { getAllRisks } from "@/services/risksService";
import assuranceActionsService from "@/services/assuranceActionsService";
import { getPolicyList } from "@/services/policyService";
import documentsService from "@/services/documentsService";
import controlsService from "@/services/controlsService";
import { useTenancy } from "@/contexts/TenancyContext";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { routePathByName } from "@/app/routes";
import { useNavigate } from 'react-router-dom';
import { getAvailableFeatures } from "@/utils/featuresUtil.js";
import ComplianceModuleMigrationService from "@/services/complianceModuleMigrationService";
import { useAuth } from "@/contexts/AuthContext";
import TermsDialog from './TermsDialog';
import CompanyContextDialog from './CompanyContextDialog';
import { getSystemInfo } from '@/js/external-js/variableModules/utils/getSystemInfo.util';
import termsContent from '@/../docs/ai-harold-terms.md?raw';
import { getCompanyContext } from '@/services/companyContextService';

// Type definition for features state
interface FeaturesState {
  advancedFeatures: {
    masterControlMapping: boolean;
  };
  experimentalFeatures: {
    aiAssistedInternalAudits: boolean;
    automatedChecks: boolean;
    customIntegrations: boolean;
    managementSystemGuide: boolean;
    aiHarold: boolean;
  };
  aiHaroldTermsAccepted: Record<string, any>;
  mcmMigrationCompleted: boolean;
  deprecatedFeatures: {
    ismsRegister: {
      link: string;
      name: string;
      description: string;
    };
    ismsManual: {
      link: string;
      name: string;
      description: string;
    };
  };
}

export default function FeaturesSection() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isValidatingHarold, setIsValidatingHarold] = useState(false);
  const [showTermsDialog, setShowTermsDialog] = useState(false);
  const [shouldShowAIFeatures, setShouldShowAIFeatures] = useState(false);
  const [pendingFeature, setPendingFeature] = useState<string | null>(null);
  const [showCompanyContextDialog, setShowCompanyContextDialog] = useState(false);
  const [isMigrating, setIsMigrating] = useState(false);
  const [termsVersion, setTermsVersion] = useState('1.0'); // Should match version in ai-harold-terms.md
  const [migrationState, setMigrationState] = useState({
    isRunning: false,
    relationshipCounts: {} as Record<string, number>,
    progress: 0,
    total: 0,
    current: 0,
    currentItem: ''
  });
  const { activeTenancy } = useTenancy();
  const [features, setFeatures] = useState({
    advancedFeatures: {
      masterControlMapping: false,
    },
    experimentalFeatures: {
      aiAssistedInternalAudits: false,
      automatedChecks: false,
      customIntegrations: false,
      managementSystemGuide: false,
      aiHarold: false,
    },
    // Metadata for AI Harold terms acceptance
    aiHaroldTermsAccepted: {},
    // Track MCM migration completion
    mcmMigrationCompleted: false,
    // Features that are deprecated and will be removed in a future release
    deprecatedFeatures: {
      ismsRegister: {
        link: '/ismsregister.html',
        name: 'ISMS Register',
        description: 'The ISMS Register feature is deprecated and will be removed in a future release. Please use the Audit Centre instead.',
      },
      ismsManual: {
        link: '/isms-manual-db.html',
        name: 'ISMS Manual',
        description: 'The ISMS Manual feature is deprecated and will be removed in a future release. Please use the Management System Guide instead.',
      }
    }
  });
  console.log('termsContent length:', termsContent?.length, termsContent?.slice(0, 60));


  const availableFeatures = getAvailableFeatures();
  const hasHaroldAI = !!availableFeatures.find(f => f.name === 'Harold AI');
  const hasMCMMigration = !!availableFeatures.find(f => f.name === 'MCM Migration');

  useEffect(() => {
    const fetchSettings = async () => {
      setIsLoading(true);
      try {
        const settings = await companyService.getCompanySettings(undefined, "features");
        setFeatures({
          advancedFeatures: {
            masterControlMapping: settings?.advancedFeatures?.masterControlMapping ?? false,
          },
          experimentalFeatures: {
            aiAssistedInternalAudits: settings?.experimentalFeatures?.aiAssistedInternalAudits ?? false,
            automatedChecks: settings?.experimentalFeatures?.automatedChecks ?? false,
            customIntegrations: settings?.experimentalFeatures?.customIntegrations ?? false,
            managementSystemGuide: settings?.experimentalFeatures?.managementSystemGuide ?? false,
            aiHarold: settings?.experimentalFeatures?.aiHarold ?? false,
          },
          aiHaroldTermsAccepted: settings?.aiHaroldTermsAccepted || {},
          mcmMigrationCompleted: settings?.mcmMigrationCompleted ?? false,
          deprecatedFeatures: {
            ismsRegister: {
              link: '/ismsregister.html',
              name: 'ISMS Register',
              description: 'The ISMS Register feature is deprecated and will be removed in a future release. Please use the Audit Centre instead.',
            },
            ismsManual: {
              link: '/isms-manual-db.html',
              name: 'ISMS Manual',
              description: 'The ISMS Manual feature is deprecated and will be removed in a future release. Please use the Management System Guide instead.',
            }
          }
        });

        setShouldShowAIFeatures(hasHaroldAI);
      } catch (error) {
        console.error("Failed to fetch company settings:", error);
        toast.error("Failed to load feature settings");
      } finally {
        setIsLoading(false);
      }
    };
    fetchSettings();
  }, []);

  // Set force override for ISMS replacement when this component is mounted
  useEffect(() => {
    if (typeof document !== 'undefined' && document.body) {
      document.body.setAttribute('data-force-isms-override', 'true');
    }
    
    // Clean up: remove the attribute when component unmounts
    return () => {
      if (typeof document !== 'undefined' && document.body) {
        document.body.removeAttribute('data-force-isms-override');
      }
    };
  }, []);

  // Check if SOA controls exist (for fresh setup detection)
  const checkSOAControlsExist = async () => {
    if (!activeTenancy?.id) return false;
    
    try {
      const controls = await controlsService.getAllControls(() => true);
      return controls && controls.length > 0;
    } catch (error) {
      console.error('Failed to check SOA controls:', error);
      return false;
    }
  };

  // Check if company context is completed
  const checkCompanyContextCompleted = async () => {
    console.log('🏢 [CONTEXT DEBUG] Checking company context completion...');
    console.log('🏢 [CONTEXT DEBUG] Active tenancy ID:', activeTenancy?.id);
    
    if (!activeTenancy?.id) {
      console.log('🏢 [CONTEXT DEBUG] ❌ No active tenancy - returning false');
      return false;
    }
    
    try {
      const companyContext = await getCompanyContext(activeTenancy.id);
      console.log('🏢 [CONTEXT DEBUG] Company context data:', companyContext);
      const isComplete = companyContext?.metadata?.onboardingComplete === true;
      console.log('🏢 [CONTEXT DEBUG] Onboarding complete:', isComplete);
      return isComplete;
    } catch (error) {
      console.error('🏢 [CONTEXT DEBUG] Failed to check company context completion:', error);
      return false;
    }
  };

  // Check if MCM migration has been completed
  const checkMCMMigrationStatus = async (currentFeatures = features) => {
    console.log('🔍 [MIGRATION DEBUG] Checking MCM migration status...');
    console.log('🔍 [MIGRATION DEBUG] Active tenancy ID:', activeTenancy?.id);
    
    if (!activeTenancy?.id) {
      console.log('🔍 [MIGRATION DEBUG] ❌ No active tenancy - returning false');
      return false;
    }
    
    try {
      // Check if MCM controls exist to determine migration status
      console.log('🔍 [MIGRATION DEBUG] Fetching MCM controls...');
      const mcmControls = await mcmService.getControls(false, activeTenancy.id);
      const hasControls = mcmControls?.Controls && mcmControls.Controls.length > 0;
      console.log('🔍 [MIGRATION DEBUG] MCM controls found:', hasControls, 'Count:', mcmControls?.Controls?.length || 0);
      
      // Check if SOA controls exist (for fresh setup detection)
      console.log('🔍 [MIGRATION DEBUG] Checking for SOA controls...');
      const hasSOAControls = await checkSOAControlsExist();
      console.log('🔍 [MIGRATION DEBUG] SOA controls found:', hasSOAControls);
      
      // Check the persisted flag
      const migrationCompleted = currentFeatures.mcmMigrationCompleted || false;
      console.log('🔍 [MIGRATION DEBUG] Migration flag from features:', migrationCompleted);
      
      // Auto-mark migration as complete for fresh setups
      if (!hasSOAControls && !migrationCompleted && !hasControls) {
        console.log('🔍 [MIGRATION DEBUG] Fresh MCM setup detected - auto-marking migration as complete');
        const updatedFeatures = {
          ...currentFeatures,
          mcmMigrationCompleted: true
        };
        
        try {
          await companyService.saveCompanySettings(undefined, "features", updatedFeatures);
          setFeatures(updatedFeatures);
          console.log("🔍 [MIGRATION DEBUG] Fresh MCM setup migration flag saved to Firebase");
          return true; // Fresh setup, migration considered complete
        } catch (saveError) {
          console.error("🔍 [MIGRATION DEBUG] Failed to save fresh MCM setup migration flag:", saveError);
          return false;
        }
      }
      
      console.log('🔍 [MIGRATION DEBUG] Final calculation:');
      console.log('🔍 [MIGRATION DEBUG] - hasControls:', hasControls);
      console.log('🔍 [MIGRATION DEBUG] - migrationCompleted:', migrationCompleted);
      console.log('🔍 [MIGRATION DEBUG] - hasSOAControls:', hasSOAControls);
      
      // Migration is complete if we have MCM controls OR the migration flag is set
      // The SOA controls check is only for fresh setup detection, not for blocking migrated instances
      const finalResult = hasControls || migrationCompleted;
      console.log('🔍 [MIGRATION DEBUG] Final result:', finalResult);
      return finalResult;
    } catch (error) {
      console.error('🔍 [MIGRATION DEBUG] Failed to check MCM migration status:', error);
      return false;
    }
  };

  // Terms content is imported directly, so no need for useEffect loading

  const handleFeatureChange = async (section, key, value) => {
    const newValue = value === "true";

    
    // Special handling for AI Harold terms acceptance
    if (key === 'aiHarold' && newValue && !features.experimentalFeatures.aiHarold) {
      console.log('🤖 [DEBUG] Attempting to enable Harold AI...');
      console.log('🤖 [DEBUG] Current features:', features);
      
      // Set loading state
      setIsValidatingHarold(true);
      
      // Check if MCM is enabled first
      console.log('🤖 [DEBUG] Checking Master Control Mapping...');
      console.log('🤖 [DEBUG] Master Control Mapping value:', features.advancedFeatures.masterControlMapping);
      if (!features.advancedFeatures.masterControlMapping) {
        console.log('🤖 [DEBUG] ❌ Master Control Mapping is NOT enabled - blocking Harold AI');
        toast.error("Master Control Mapping must be enabled before using AI features. Please enable Master Control Mapping first.");
        setIsValidatingHarold(false);
        return;
      }
      console.log('🤖 [DEBUG] ✅ Master Control Mapping is enabled');
      
      // Check if MCM migration has been completed
      console.log('🤖 [DEBUG] Checking MCM migration status...');
      checkMCMMigrationStatus(features).then(migrationCompleted => {
        console.log('🤖 [DEBUG] Migration check result:', migrationCompleted);
        if (!migrationCompleted) {
          console.log('🤖 [DEBUG] ❌ MCM migration not completed - blocking Harold AI');
          toast.error("MCM migration must be completed before using AI features. Please migrate your controls from SOA to MCM first.");
          setIsValidatingHarold(false);
          return;
        }
        console.log('🤖 [DEBUG] ✅ MCM migration completed');
        
        // Check if company context is completed
        console.log('🤖 [DEBUG] Checking company context completion...');
        checkCompanyContextCompleted().then(contextCompleted => {
          console.log('🤖 [DEBUG] Company context completed:', contextCompleted);
          if (!contextCompleted) {
            console.log('🤖 [DEBUG] ❌ Company context not completed - showing terms dialog first');
            // Set pending feature and show terms dialog first
            setPendingFeature(key);
            setShowTermsDialog(true);
            setIsValidatingHarold(false);
            return;
          }
          console.log('🤖 [DEBUG] ✅ Company context completed');
          
          // All checks passed - show terms dialog
          console.log('🤖 [DEBUG] ✅ All checks passed - showing terms dialog');
          setPendingFeature(key);
          setShowTermsDialog(true);
          setIsValidatingHarold(false);
        }).catch(error => {
          console.error('🤖 [DEBUG] Error checking company context:', error);
          toast.error("Failed to validate company context");
          setIsValidatingHarold(false);
        });
      }).catch(error => {
        console.error('🤖 [DEBUG] Error checking migration status:', error);
        toast.error("Failed to validate migration status");
        setIsValidatingHarold(false);
      });
      
      return; // Exit early since we're handling async
    }

    // Log when AI Harold is disabled
    if (key === 'aiHarold' && !newValue && features.experimentalFeatures.aiHarold) {
      try {
        const systemInfo = await getSystemInfo();
        const disableData = {
          user: user?.uid,
          timestamp: new Date().toISOString(),
          systemInfo: {
            userAgent: systemInfo.userAgent,
            platform: systemInfo.platform,
            language: systemInfo.language,
            ip: systemInfo.ip
          }
        };
        
        console.log("AI Harold disabled", disableData);
        
        // Store disable metadata in Firebase
        if (window.companyID && user?.uid) {
          // Using timestamp as unique ID instead of user ID to track each action separately
          const timestampId = new Date().getTime().toString();
          const updatedFeatures = {
            ...features,
            experimentalFeatures: {
              ...features.experimentalFeatures,
              [key]: newValue
            },
            aiHaroldTermsAccepted: {
              ...(features.aiHaroldTermsAccepted || {}),
              [timestampId]: {
                ...disableData,
                userId: user.uid,
                action: 'disabled'
              }
            }
          };
          
          setFeatures(updatedFeatures);
          return;
        }
      } catch (error) {
        console.error("Failed to collect system information when disabling AI Harold:", error);
      }
    }

    setFeatures(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: newValue
      }
    }));
  };

  const handleAcceptTerms = async () => {
    // Collect system information when terms are accepted
    try {
      const systemInfo = await getSystemInfo();
      console.log("System information collected:", systemInfo);
      
      // Log terms acceptance with system info
      const acceptanceData = {
        version: termsVersion,
        user: user?.uid,
        timestamp: new Date().toISOString(),
        systemInfo: {
          userAgent: systemInfo.userAgent,
          platform: systemInfo.platform,
          language: systemInfo.language,
          ip: systemInfo.ip
        }
      };
      
      console.log("AI Harold Terms accepted", acceptanceData);
      
      // Store acceptance metadata in Firebase
      if (window.companyID && user?.uid) {
        try {
          // Create updated features object with terms acceptance metadata
          // Using timestamp as unique ID instead of user ID to track each acceptance separately
          const timestampId = new Date().getTime().toString();
          const updatedFeatures = {
            ...features,
            // NOTE: Don't enable AI feature yet - wait until company context is completed
            aiHaroldTermsAccepted: {
              ...(features.aiHaroldTermsAccepted || {}),
              [timestampId]: {
                ...acceptanceData,
                userId: user.uid
              }
            }
          };
          
          // Save terms acceptance metadata and wait for completion
          await companyService.saveCompanySettings(undefined, "features", updatedFeatures);
          console.log("AI Harold terms acceptance metadata saved to Firebase");
          
          // Update local state after successful save
          setFeatures(updatedFeatures);
          
        } catch (firebaseError) {
          console.error("Failed to save terms acceptance metadata to Firebase:", firebaseError);
          toast.error("Failed to save terms acceptance");
          return; // Don't proceed if terms acceptance failed
        }
      }
    } catch (error) {
      console.error("Failed to collect system information:", error);
      toast.error("Failed to collect system information");
      return; // Don't proceed if system info collection failed
    }

    if (pendingFeature) {
      console.log("Accepting terms for feature:", pendingFeature);
      
      // Check if company context is still needed
      const contextCompleted = await checkCompanyContextCompleted();
      if (!contextCompleted) {
        // Show company context dialog after terms acceptance
        setShowTermsDialog(false);
        setShowCompanyContextDialog(true);
        return; // Don't clear pendingFeature yet, we'll enable AI after context is completed
      } else {
        // Company context is already completed, enable the feature now
        await enableAIFeature();
      }
    }
    setShowTermsDialog(false);
    setPendingFeature(null);
  };

  const handleDeclineTerms = () => {
    setShowTermsDialog(false);
    setPendingFeature(null);
  };

  // Handle company context completion - enable AI feature after context is completed
  const handleCompanyContextComplete = async () => {
    await enableAIFeature();
  };

  // Enable AI feature (called after both terms acceptance and company context completion)
  const enableAIFeature = async () => {
    if (pendingFeature) {
      console.log("Enabling AI feature:", pendingFeature);
      try {
        // Create updated features object with AI feature enabled
        const updatedFeatures = {
          ...features,
          experimentalFeatures: {
            ...features.experimentalFeatures,
            [pendingFeature]: true
          }
        };
        
        // Update the local state
        setFeatures(updatedFeatures);
        
        // Save to Firebase
        await companyService.saveCompanySettings(undefined, "features", updatedFeatures);
        console.log("AI feature enabled and saved successfully");
        toast.success("AI Harold enabled successfully!");
      } catch (error) {
        console.error("Failed to enable AI feature:", error);
        toast.error("Failed to enable AI feature");
      }
    }
    setPendingFeature(null);
  };

  const renderTermsDialog = () => {
    // Import the terms content directly from the markdown file
    return (
      <TermsDialog 
        open={showTermsDialog}
        onClose={handleDeclineTerms}
        onAccept={handleAcceptTerms}
        termsContent={termsContent}
        version={termsVersion}
      />
    );
  };

  // Types for filter options
  interface FilterByControlOptions {
    checkRelatedControls?: boolean;
    checkSelectedControls?: boolean;
    checkRelatedTasks?: boolean;
    checkRelatedComponents?: boolean;
    checkProcRef?: boolean;
    checkFramework?: boolean;
    checkRelatedAssuranceTasks?: boolean;
    checkProcRefDirect?: boolean;
    checkTasks?: boolean;
    checkISO27001?: boolean;
    checkNIST?: boolean;
    checkControls?: boolean;
    checkAssets?: boolean;
    checkRisks?: boolean;
    checkControlID?: boolean;
    checkPolicies?: boolean;
    checkDocuments?: boolean;
  }

  // Default options for different item types
  const defaultOptions: Record<string, FilterByControlOptions> = {
    asset: {
      checkRelatedControls: true,
      checkSelectedControls: true,
      checkRelatedTasks: true,
      checkRelatedComponents: true,
      checkAssets: true,
    },
    risk: {
      checkRelatedControls: true,
      checkSelectedControls: true,
      checkRisks: true,
    },
    task: {
      checkProcRef: true,
      checkSelectedControls: true,
      checkRelatedAssuranceTasks: true,
      checkTasks: true,
      checkControlID: true,
      checkRelatedControls: true,
    },
    policy: {
      checkRelatedControls: true,
      checkSelectedControls: true,
      checkISO27001: true,
      checkPolicies: true
    },
    document: {
      checkRelatedControls: true,
      checkSelectedControls: true,
      checkProcRefDirect: true,
      checkDocuments: true
    },
    control: {
      checkRelatedControls: true,
      checkSelectedControls: true,
      checkISO27001: true,
      checkNIST: true,
      checkControls: true
    }
  };

  // Helper function to match a value against a control value
  const matchControlValue = (value: any, controlValue: string): boolean => {
    // Convert controlValue to string for comparison
    const controlValueStr = String(controlValue);

    // Handle arrays
    if (Array.isArray(value)) {
      return value.some(item => matchControlValue(item, controlValueStr));
    }

    // Handle strings and numbers
    if (typeof value === 'string' || typeof value === 'number') {
      return String(value) === controlValueStr;
    }

    // Handle objects
    if (value && typeof value === 'object') {
      // Check common control ID properties
      const controlIdProperties = ['controlID', 'controlNumber', 'value', 'id', 'controlId'];
      return controlIdProperties.some(prop =>
        value[prop] !== undefined && String(value[prop]) === controlValueStr
      );
    }

    return false;
  };

  // Helper function to filter items by related controls with type-safe options
  const filterByControl = (
    items: any[],
    controlValue: string,
    itemType?: keyof typeof defaultOptions
  ): any[] => {
    const options = itemType && defaultOptions[itemType]
      ? { ...defaultOptions[itemType] }
      : {
        // If no type specified, check all properties
        checkRelatedControls: true,
        checkSelectedControls: true,
        checkRelatedTasks: true,
        checkControlID: true,
        checkRelatedComponents: true,
        checkProcRef: true,
        checkFramework: true,
        checkRelatedAssuranceTasks: true,
        checkProcRefDirect: true,
        checkTasks: true,
        checkISO27001: true,
        checkNIST: true,
        checkControls: true,
        checkAssets: true,
        checkRisks: true,
        checkPolicies: true,
        checkDocuments: true
      };

    return items.filter(item => {
      if (itemType === 'policy') {
        // console.log(`[MCM Migration@${itemType}] Filtering item:`, item);
      }
      const related: Array<{ type: string, value: any }> = [];

      // Helper function to add related items with their types
      const addRelated = (type: string, value: any) => {
        if (value) {
          related.push({ type, value });
        }
      };

      // Add all potential related items
      if (options.checkRelatedControls) {
        if (itemType === 'policy') {
          // Special handling for policies which use relatedControls directly
          if ((item as any).relatedControls) {
            console.log(`[Policy ${(item as any).policy_name || (item as any).id}] Related controls:`, (item as any).relatedControls);
            // Check if the controlValue exists in the relatedControls array
            const relatedControls = Array.isArray((item as any).relatedControls)
              ? (item as any).relatedControls
              : [];

            // If the control value is in the related controls, include this policy
            if (relatedControls.includes(controlValue) ||
              relatedControls.some((rc: any) => String(rc) === String(controlValue))) {
              addRelated('relatedControls', (item as any).relatedControls);
              return true; // Include this policy in the results
            }
          }
          return false; // Skip this policy if no match found
        } else {
          // Default case for other item types
          if ((item as any).relatedControls) {
            addRelated('relatedControls', (item as any).relatedControls);
          }
        }
      }

      if (options.checkControlID) {
        addRelated('controlID', (item as any).controlID);
      }

      if (options.checkSelectedControls) addRelated('selectedControls', (item as any).selectedControls);
      if (options.checkRelatedTasks) addRelated('relatedTasks', (item as any).relatedTasks);
      if (options.checkRelatedComponents) addRelated('relatedComponents', (item as any).relatedComponents);
      if (options.checkProcRef) addRelated('procRef', (item as any).checkDetail?.procRef);
      if (options.checkFramework) addRelated('framework', (item as any).checkDetail?.framework);
      if (options.checkRelatedAssuranceTasks) addRelated('relatedAssuranceTasks', (item as any).relatedAssuranceTasks);
      if (options.checkProcRefDirect) addRelated('procRef', (item as any).procRef);

      if (options.checkTasks && (item as any).tasks) {
        addRelated('tasks', (item as any).tasks);
        addRelated('tasks', (item as any).assuranceTasks);
        addRelated('tasks', (item as any).relatedAssuranceTasks);
        addRelated('tasks', (item as any).relatedTasks);
        addRelated('tasks', (item as any).relatedControls);
        addRelated('tasks', (item as any).controlID);
        addRelated('tasks', (item as any).procRef);
        addRelated('tasks', (item as any).checkDetail?.procRef || []);
      }

      if (options.checkISO27001) {
        addRelated('iso27001', [...(item as any)?.iso270012022AnnexAControls || [], ...(item as any)?.mandatoryISO270012022Control || []]);
      }

      if (options.checkNIST) addRelated('nist', (item as any).nist80053Rev5);
      if (options.checkControls) addRelated('controls', (item as any).controls);
      if (options.checkAssets) {
        addRelated('assets', (item as any).assets);
        addRelated('assets', (item as any).relatedAssets);
      }
      if (options.checkRisks) {
        addRelated('risks', (item as any).risks);
        addRelated('risks', (item as any).relatedRisks);
      }
      if (options.checkPolicies) {
        addRelated('policies', (item as any).policies);
        addRelated('policies', (item as any).relatedPolicies);
      }
      if (options.checkDocuments) {
        // Handle both documents and relatedDocuments, ensuring we have an array of document objects
        const docs = [
          ...(Array.isArray((item as any).documents) ? (item as any).documents : []),
          ...(Array.isArray((item as any).relatedDocuments) ? (item as any).relatedDocuments : [])
        ];

        // Only add if we have documents to process
        if (docs.length > 0) {
          console.log(`Found ${docs.length} documents for item:`, item);
          addRelated('documents', docs);
        }
      }

      // Initialize match counts for this item
      const matchCounts: Record<string, number> = {};

      // Check each related item for matches
      const hasMatch = related.some(({ type, value }) => {
        if (!value) return false;

        let foundMatch = false;

        // Helper to check if a single value matches and update counts
        const checkValue = (val: any): boolean => {
          if (matchControlValue(val, controlValue)) {
            if (!matchCounts[type]) matchCounts[type] = 0;
            matchCounts[type]++;
            return true;
          }
          return false;
        };

        // Handle arrays
        if (Array.isArray(value)) {
          value.forEach(item => {
            try {
              // Special handling for document objects
              if (item && typeof item === 'object' && 'docHash' in item) {
                // This is a document object, check its properties that might contain the control value
                const docPropsToCheck = ['docHash', 'id', 'docId', 'documentId', 'relatedControls', 'selectedControls', 'procRef'];
                const docMatch = docPropsToCheck.some(prop =>
                  item[prop] && matchControlValue(item[prop], controlValue)
                );

                if (docMatch) {
                  console.log('Document match found:', item);
                  if (!matchCounts[type]) matchCounts[type] = 0;
                  matchCounts[type]++;
                  foundMatch = true;
                }
              }
              // Handle nested arrays/objects
              else if (Array.isArray(item) || (item && typeof item === 'object')) {
                const flatItems = Array.isArray(item)
                  ? item.flat(Infinity)
                  : Object.values(item).flat(Infinity);
                flatItems.forEach(nestedItem => checkValue(nestedItem));
              } else if (checkValue(item)) {
                foundMatch = true;
              }
            } catch (error) {
              console.error('Error processing item:', item, error);
            }
          });
          return foundMatch;
        }

        // Handle objects
        if (typeof value === 'object') {
          Object.values(value).forEach(val => {
            if (checkValue(val)) {
              foundMatch = true;
            }
          });
          return foundMatch;
        }

        // Handle direct values
        return checkValue(value);
      });

      // Update relationship counts in state if we have any matches
      if (Object.keys(matchCounts).length > 0) {
        console.log('Updating relationship counts:', matchCounts); // Debug log
        setMigrationState(prev => {
          const newCounts = {
            ...prev.relationshipCounts,
            ...Object.entries(matchCounts).reduce((acc, [type, count]) => ({
              ...acc,
              [type]: (prev.relationshipCounts[type] || 0) + (count || 0)
            }), {})
          };
          // console.log('New relationship counts:', newCounts); // Debug log
          return {
            ...prev,
            relationshipCounts: newCounts
          };
        });
      }

      return hasMatch;
    });
  };

  const migrateSOAToMCM = async () => {
    if (!activeTenancy?.id) {
      toast.error('No tenancy selected');
      return;
    }

    setMigrationState({
      isRunning: true,
      progress: 0,
      total: 0,
      current: 0,
      currentItem: 'Preparing migration...',
      relationshipCounts: {}
    });
    try {
      // 1. Get all controls from SOA
      setMigrationState(prev => ({ ...prev, currentItem: 'Loading controls...', relationshipCounts: prev.relationshipCounts || {} }));
      const controls = await controlsService.getAllControls(() => true);

      // 2. Get all related entities
      setMigrationState(prev => ({ ...prev, currentItem: 'Loading related entities...', relationshipCounts: prev.relationshipCounts || {} }));
      const [assets, risks, actions, policies, documents] = await Promise.all([
        assetsService.getAssets(),
        getAllRisks(activeTenancy?.id || ''),
        assuranceActionsService.getAllTasks(),
        getPolicyList(activeTenancy?.id || ''),
        documentsService.getDocuments()
      ]);

      // 3. Create MCM controls with relationships
      setMigrationState(prev => ({
        ...prev,
        total: controls.length,
        current: 0,
        currentItem: 'Processing controls...',
        relationshipCounts: prev.relationshipCounts || {}
      }));

      const mcmControls = controls.map((control, index) => {
        setMigrationState(prev => ({
          ...prev,
          current: index,
          progress: Math.round((index / controls.length) * 100),
          currentItem: `Processing control ${index + 1} of ${controls.length}: ${control.label || control.value}`,
          relationshipCounts: prev.relationshipCounts || {}
        }));

        // DEBUG Log for ALL THE THINGS
        console.log('Processing control:', control);
        console.log('Related assets:', filterByControl(assets, control.value, 'asset'));
        console.log('Related risks:', filterByControl(risks, control.value, 'risk'));
        console.log('Related tasks:', filterByControl(actions, control.value, 'task'));
        console.log('Related policies:', filterByControl(policies, control.value, 'policy'));
        console.log('Related documents:', filterByControl(documents, control.value, 'document'));

        // Find related entities with type-safe filtering
        const relatedAssets = filterByControl(assets, control.value, 'asset').map(a => ({
          id: `internal:asset:${(a as any).id || (a as any).assetID}`,
          domain: 'Asset',
          scheme: 'internal',
          ref: (a as any).id || (a as any).assetID,
          title: (a as any).name || (a as any).assetDesc || 'Unnamed Asset'
        }));

        const relatedRisks = filterByControl(risks, control.value, 'risk').map(r => ({
          id: `internal:risk:${r.id || r.riskID}`,
          domain: 'Risk',
          scheme: 'internal',
          ref: r.id || r.riskID,
          title: r.title || r.risk || r.riskName || 'Unnamed Risk'
        }));

        const relatedActions = filterByControl(actions, control.value, 'task').map(a => ({
          id: `internal:assuranceTask:${a.id || a.assID}`,
          domain: 'Assurance Task',
          scheme: 'internal',
          ref: (a as any).id || (a as any).assID,
          title: (a as any).checkDetail.check || 'Unnamed Task'
        }));

        const relatedPolicies = filterByControl(policies, control.value, 'policy').map(p => ({
          id: `internal:policy:${p.id || p.policy_id}`,
          domain: 'Policy',
          scheme: 'internal',
          ref: p.id || p.policy_id,
          title: (p as any).policy_name || 'Unnamed Policy'
        }));

        const relatedDocuments = filterByControl(documents, control.value, 'document').map(d => ({
          id: `internal:document:${d.id || d.docID}`,
          domain: 'Document',
          scheme: 'internal',
          ref: d.id || d.docID,
          title: d.fileName
        }));

        const relatedControls = filterByControl(controls, control.value, 'control').map(c => ({
          id: `internal:control:${c.id || c.controlID}`,
          domain: 'Control',
          scheme: 'internal',
          ref: c.id || c.controlID,
          title: c.controlName
        }));

        // Create MCM control
        return {
          controlID: control.value,
          controlName: control.label,
          controlDescription: control.description,
          standard: control.standard,
          status: 'Draft',
          linkedComponents: [
            ...relatedAssets,
            ...relatedRisks,
            ...relatedActions,
            ...relatedPolicies,
            ...relatedDocuments,
            ...relatedControls
          ]
        };
      });

      // 4. Save to MCM
      setMigrationState(prev => ({
        ...prev,
        current: 0,
        currentItem: 'Saving controls to MCM...',
        relationshipCounts: prev.relationshipCounts || {}
      }));

      for (let i = 0; i < mcmControls.length; i++) {
        const control = mcmControls[i];
        await mcmService.createControl(control, activeTenancy.id);

        setMigrationState(prev => ({
          ...prev,
          current: i + 1,
          progress: Math.round(((i + 1) / mcmControls.length) * 100),
          currentItem: `Saving control ${i + 1} of ${mcmControls.length}`,
          relationshipCounts: prev.relationshipCounts || {}
        }));
      }

      setMigrationState({
        isRunning: false,
        progress: 100,
        total: mcmControls.length,
        current: mcmControls.length,
        currentItem: 'Migration complete!',
        relationshipCounts: {}
      });

      toast.success(`Successfully migrated ${mcmControls.length} controls to MCM`);

      // Mark migration as completed in features
      setFeatures(prev => ({
        ...prev,
        mcmMigrationCompleted: true
      }));

      // Save the migration completion flag to backend
      try {
        const updatedFeatures = {
          ...features,
          mcmMigrationCompleted: true
        };
        await companyService.saveCompanySettings(undefined, "features", updatedFeatures);
        console.log("MCM migration completion flag saved to Firebase");
      } catch (saveError) {
        console.error("Failed to save MCM migration completion flag:", saveError);
      }

      // Reset after a short delay
      setTimeout(() => {
        setMigrationState({
          isRunning: false,
          progress: 0,
          total: 0,
          current: 0,
          currentItem: '',
          relationshipCounts: {}
        });
      }, 3000);
    } catch (error) {
      console.error('Migration error:', error);
      toast.error(`Migration failed: ${error.message}`);
      setMigrationState({
        isRunning: false,
        progress: 100,
        total: 0,
        current: 0,
        currentItem: 'Migration completed!',
        relationshipCounts: {}
      });
    }
  };

  const handleSaveSettings = async () => {
    setIsSaving(true);
    try {
      console.log("Saving features to Firebase:", features);
      console.log("AI Harold enabled:", features.experimentalFeatures.aiHarold);
      console.log("Company ID:", window.companyID);

      // Check if AI Harold is being enabled and run migration if needed
      if (features.experimentalFeatures.aiHarold && window.companyID) {
        console.log("AI Harold is enabled, running compliance modules migration");
        setIsMigrating(true);
        try {
          await ComplianceModuleMigrationService.fixComplianceModules(window.companyID);
          console.log("Compliance modules migration completed during save");
          toast.success("AI feature enabled and compliance modules updated");
        } catch (migrationError) {
          console.error("Failed to migrate compliance modules during save:", migrationError);
          toast.error("Failed to update compliance modules. Please contact support.");
        }
        setIsMigrating(false);
      } else {
        console.log("Migration not triggered - AI Harold:", features.experimentalFeatures.aiHarold, "Company ID:", window.companyID);
      }

      await companyService.saveCompanySettings(undefined, "features", features);
      if (!features.experimentalFeatures.aiHarold) {
        toast.success("Settings saved successfully");
      }
      window.location.reload();
    } catch (error) {
      console.error("Failed to save company settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setIsSaving(false);
    }
  };


  return (
    <div className="space-y-6">
      {renderTermsDialog()}
      <CompanyContextDialog 
        open={showCompanyContextDialog}
        onClose={() => {
          setShowCompanyContextDialog(false);
          setPendingFeature(null); // Clear pending feature if user cancels
        }}
        onComplete={handleCompanyContextComplete}
      />
      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <FontAwesomeIcon icon={faSpinner} spin className="text-primary mr-2" />
          <span>Loading settings...</span>
        </div>
      ) : (
        <>
          <Card className="mb-8">
            <CardHeader className="flex flex-row items-center gap-2">
              <FontAwesomeIcon icon={faGear} className="text-primary" />
              <CardTitle className="text-lg">Advanced Features</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <label htmlFor="masterControlMappingInput" className="flex items-center gap-2 text-base font-medium text-foreground">
                  <FontAwesomeIcon icon={faDiagramProject} className="text-primary" />
                  Master Control Mapping
                </label>
                <div className="flex flex-col space-y-4">
                  <Select
                    value={features.advancedFeatures.masterControlMapping ? 'enabled' : 'disabled'}
                    onValueChange={val => handleFeatureChange('advancedFeatures', 'masterControlMapping', val === 'enabled' ? 'true' : 'false')}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="enabled">Enabled</SelectItem>
                      <SelectItem value="disabled">Disabled</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-muted-foreground mt-1">
                    Enable Master Control Mapping to manage relationships between controls and other entities.
                  </p>
                  {/* Show migration option only when MCM is enabled AND migration hasn't been completed */}
                  {hasMCMMigration && features.advancedFeatures.masterControlMapping && !features.mcmMigrationCompleted && (
                    <div className="mt-4 pt-4 border-t">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium">Migrate from SOA to MCM</h4>
                          <p className="text-sm text-muted-foreground">
                            Convert your existing SOA controls to the new MCM format.
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          onClick={migrateSOAToMCM}
                          disabled={migrationState.isRunning}
                        >
                          {migrationState.isRunning ? (
                            <>
                              <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                              Migrating...
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faExchangeAlt} className="mr-2" />
                              Migrate Now
                            </>
                          )}
                        </Button>
                      </div>
                      {migrationState.isRunning && (
                        <div className="mt-4 space-y-2">
                          <div className="flex justify-between text-sm text-muted-foreground">
                            <span>{migrationState.currentItem}</span>
                            <span>{migrationState.progress}%</span>
                          </div>
                          <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-primary transition-all duration-300 ease-out"
                              style={{ width: `${migrationState.progress}%` }}
                            />
                          </div>
                          <div className="text-xs text-muted-foreground text-right">
                            {migrationState.current} of {migrationState.total} controls processed
                          </div>
                          {Object.entries(migrationState.relationshipCounts).length > 0 && (
                            <div className="mt-2 text-xs text-muted-foreground">
                              <div className="font-medium mb-1">Relationships found:</div>
                              <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                                {Object.entries(migrationState.relationshipCounts)
                                  .filter(([_, count]) => count > 0)
                                  .map(([key, count]) => {
                                    // Format the key for display
                                    const displayKey = key
                                      .replace(/([A-Z])/g, ' $1') // Add space before capital letters
                                      .replace(/(?:^|\s)\w/g, match => match.toUpperCase()) // Capitalize first letter of each word
                                      .trim();

                                    return (
                                      <div key={key} className="flex justify-between">
                                        <span>{displayKey}:</span>
                                        <span className="font-mono">{count}</span>
                                      </div>
                                    );
                                  })}

                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {features.advancedFeatures.masterControlMapping && (
                    <a
                      href={routePathByName("mcm")}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(routePathByName("mcm"));
                      }}
                      className="flex items-center gap-2 text-amber-500 font-medium mt-3 hover:text-amber-600 transition-colors cursor-pointer"
                    >
                      Configure Master Control Mapping
                      <FontAwesomeIcon icon={faGear} className="ml-1" />
                    </a>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="mb-8">
            <CardHeader className="flex flex-row items-center gap-2">
              <FontAwesomeIcon icon={faFlask} className="text-purple-500" />
              <CardTitle className="text-lg">Deprecated Features</CardTitle>
            </CardHeader>
            <CardContent>
              <Alert variant="default" className="mb-6">
                <FontAwesomeIcon icon={faInfoCircle} className="text-amber-600 dark:text-amber-400" />
                <AlertDescription>
                  The features below are deprecated and will be removed in a future release. Please use the recommended alternatives mentioned in each description.
                </AlertDescription>
              </Alert>
              <div className="space-y-6">
                {Object.values(features.deprecatedFeatures).map((feature, index) => (
                  <Card key={index} className="bg-muted/50 border-l-4">
                    <CardHeader className="flex flex-row items-center gap-2">
                      <FontAwesomeIcon icon={faInfoCircle} className="text-amber-600 dark:text-amber-400" />
                      <CardTitle className="text-base font-medium text-foreground">{feature.name}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground mt-2">{feature.description}</p>
                      {feature.link && (
                      <a href={feature.link} className="flex items-center gap-2 text-amber-500 font-medium mt-3 hover:text-amber-600 transition-colors">
                        Open {feature.name}
                        <FontAwesomeIcon icon={faArrowRight} className="ml-1" />
                      </a>
                    )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
          <Card className="mb-8">
            <CardHeader className="flex flex-row items-center gap-2">
              <FontAwesomeIcon icon={faFlask} className="text-purple-500" />
              <CardTitle className="text-lg">Experimental Features</CardTitle>
            </CardHeader>
            <CardContent>
              <Alert variant="default" className="mb-6">
                <FontAwesomeIcon icon={faInfoCircle} className="text-amber-600 dark:text-amber-400" />
                <AlertDescription>
                  Experimental features are in beta testing and may not be fully stable. Use with caution.
                </AlertDescription>
              </Alert>

              {shouldShowAIFeatures ?
                (<div className="space-y-4">
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <h4 className="font-medium flex items-center gap-2">
                        <FontAwesomeIcon icon={faRobot} className="text-purple-500" />
                        AI Harold (Beta)
                      </h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        Enable AI-powered assistance with Harold, your smart business companion.
                      </p>
                    </div>
                    <Select
                      value={features?.experimentalFeatures?.aiHarold ? 'enabled' : 'disabled'}
                      onValueChange={val => handleFeatureChange('experimentalFeatures', 'aiHarold', val === 'enabled' ? 'true' : 'false')}
                      disabled={isValidatingHarold}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue placeholder={isValidatingHarold ? "Validating..." : "Select status"} />
                        {isValidatingHarold && (
                          <FontAwesomeIcon icon={faSpinner} spin className="ml-2" />
                        )}
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="enabled">Enabled</SelectItem>
                        <SelectItem value="disabled">Disabled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {features?.experimentalFeatures?.aiHarold && (
                    <Alert variant="default" className="mt-4">
                      <FontAwesomeIcon icon={faCheck} className="text-green-500" />
                      <AlertDescription>
                        AI Harold is now enabled. You can access it from the main navigation.
                      </AlertDescription>
                    </Alert>
                  )}
                </div>) : null}
            </CardContent>
          </Card>
          <CardFooter className="flex justify-center mt-8">
            <Button onClick={handleSaveSettings} disabled={isSaving} className="px-6 py-3 text-lg">
              {isSaving ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  Saving...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faCheck} />
                  Save All Features
                </>
              )}
            </Button>
          </CardFooter>
        </>
      )}
    </div>
  )
}
